import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileDown, Camera, Paperclip, X } from 'lucide-react';
import jsPDF from 'jspdf';
import authFetch from '../../utils/authFetch';
import { PropertyBadge, PropertyFilter, PropertySelect, STR_PROPERTIES, getStrProperty, strBasePath, strPropertyQuery, useStrProperty } from './properties';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';
const VAT_RATE = 0.22;

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatDateEu = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = String(isoDate).slice(0, 10).split('-');
  if (!year || !month || !day) return String(isoDate);
  return `${Number(day)}.${Number(month)}.${year}`;
};

const FinancialsPage = () => {
  const navigate = useNavigate();
  // Set on a property's own page; null on the combined page, which adds the filter and Property column.
  const property = useStrProperty();
  const fixedKey = property?.key;
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [formProperty, setFormProperty] = useState('');

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [description, setDescription] = useState('');
  const [date, setDate] = useState(todayIso());
  const [transactionType, setTransactionType] = useState('expense'); // 'expense' | 'income'
  const [expenseType, setExpenseType] = useState('private'); // 'private' | 'company'
  const [vatMode, setVatMode] = useState('withVat'); // 'withVat' | 'withoutVat'
  const [amountInput, setAmountInput] = useState('');
  const [pendingFile, setPendingFile] = useState(null);

  // Per-row attachment upload state
  const [uploadingId, setUploadingId] = useState(null);

  // Filters
  const [filterType, setFilterType] = useState('all'); // 'all' | 'private' | 'company'
  const [filterKind, setFilterKind] = useState('all'); // 'all' | 'expense' | 'income'
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  // Mobile: which row's actions (delete/invoice) are expanded
  const [expandedId, setExpandedId] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadExpenses = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/expenses${strPropertyQuery(fixedKey)}`);
      if (!response.ok) throw new Error('Failed to load expenses');
      const data = await response.json();
      setExpenses(data.expenses || []);
    } catch (err) {
      setError(err.message || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedKey]);

  const amountNumber = Number.parseFloat(amountInput);
  const hasValidAmount = !Number.isNaN(amountNumber) && amountNumber >= 0;

  // amountWithVat stays null when the amount was entered without VAT (no VAT applicable to that transaction)
  let amountWithVat = null;
  let amountWithoutVat = null;
  if (expenseType === 'company' && hasValidAmount) {
    if (vatMode === 'withVat') {
      amountWithVat = amountNumber;
      amountWithoutVat = amountNumber / (1 + VAT_RATE);
    } else {
      amountWithoutVat = amountNumber;
      amountWithVat = null;
    }
  }

  const resetForm = () => {
    setDescription('');
    setDate(todayIso());
    setTransactionType('expense');
    setExpenseType('private');
    setVatMode('withVat');
    setAmountInput('');
    setPendingFile(null);
    setFormProperty('');
  };

  const uploadAttachment = async (expenseId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await authFetch(`${API_BASE}/api/short-term-rentals/expenses/${expenseId}/attachment`, {
      method: 'POST',
      body: formData
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || 'Failed to upload attachment');
    }
    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim() || !date || !hasValidAmount) {
      setError('Please fill in description, date, and a valid amount.');
      return;
    }
    const propertyKey = fixedKey || formProperty;
    if (!propertyKey) {
      setError('Select a property for this entry.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        propertyKey,
        description: description.trim(),
        date,
        expenseType,
        transactionType,
        amountWithVat: expenseType === 'company' ? amountWithVat : null,
        amountWithoutVat: expenseType === 'company' ? amountWithoutVat : amountNumber
      };

      const response = await authFetch(`${API_BASE}/api/short-term-rentals/expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to save entry');
      }

      const data = await response.json();

      if (pendingFile) {
        try {
          await uploadAttachment(data.expense.id, pendingFile);
        } catch (uploadErr) {
          setError(uploadErr.message || 'Entry saved, but the attachment failed to upload.');
        }
      }

      resetForm();
      await loadExpenses();
    } catch (err) {
      setError(err.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleRowFileSelected = async (expenseId, file) => {
    if (!file) return;
    setUploadingId(expenseId);
    setError('');
    try {
      const data = await uploadAttachment(expenseId, file);
      setExpenses((prev) => prev.map((exp) => (exp.id === expenseId ? data.expense : exp)));
    } catch (err) {
      setError(err.message || 'Failed to upload attachment');
    } finally {
      setUploadingId(null);
    }
  };

  const handleDownloadAttachment = async (expenseId, originalName) => {
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/expenses/${expenseId}/attachment`);
      if (!response.ok) throw new Error('Failed to download attachment');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName || 'invoice';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download attachment');
    }
  };

  const handleDeleteAttachment = async (expenseId) => {
    if (!window.confirm('Remove this attachment?')) return;
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/expenses/${expenseId}/attachment`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to remove attachment');
      const data = await response.json();
      setExpenses((prev) => prev.map((exp) => (exp.id === expenseId ? data.expense : exp)));
    } catch (err) {
      setError(err.message || 'Failed to remove attachment');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      const response = await authFetch(`${API_BASE}/api/short-term-rentals/expenses/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete entry');
      setExpenses((prev) => prev.filter((exp) => exp.id !== id));
    } catch (err) {
      setError(err.message || 'Failed to delete entry');
    }
  };

  const startEdit = (exp) => {
    setExpandedId(exp.id);
    setEditingId(exp.id);
    setEditDraft({
      propertyKey: exp.property_key,
      description: exp.description,
      date: exp.date ? String(exp.date).slice(0, 10) : todayIso(),
      expenseType: exp.expense_type,
      transactionType: exp.transaction_type,
      vatMode: exp.expense_type === 'company' && exp.amount_with_vat === null ? 'withoutVat' : 'withVat',
      amountInput: exp.expense_type === 'company'
        ? String(exp.amount_with_vat !== null && exp.amount_with_vat !== undefined ? exp.amount_with_vat : exp.amount_without_vat)
        : String(exp.amount_without_vat)
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const editAmountNumber = editDraft ? Number.parseFloat(editDraft.amountInput) : NaN;
  const editHasValidAmount = editDraft && !Number.isNaN(editAmountNumber) && editAmountNumber >= 0;

  let editAmountWithVat = null;
  let editAmountWithoutVat = null;
  if (editDraft && editDraft.expenseType === 'company' && editHasValidAmount) {
    if (editDraft.vatMode === 'withVat') {
      editAmountWithVat = editAmountNumber;
      editAmountWithoutVat = editAmountNumber / (1 + VAT_RATE);
    } else {
      editAmountWithoutVat = editAmountNumber;
      editAmountWithVat = null;
    }
  }

  const handleSaveEdit = async () => {
    if (!editDraft || !editDraft.description.trim() || !editDraft.date || !editHasValidAmount) {
      setError('Please fill in description, date, and a valid amount.');
      return;
    }

    setEditSaving(true);
    setError('');
    try {
      const payload = {
        propertyKey: editDraft.propertyKey,
        description: editDraft.description.trim(),
        date: editDraft.date,
        expenseType: editDraft.expenseType,
        transactionType: editDraft.transactionType,
        amountWithVat: editDraft.expenseType === 'company' ? editAmountWithVat : null,
        amountWithoutVat: editDraft.expenseType === 'company' ? editAmountWithoutVat : editAmountNumber
      };

      const response = await authFetch(`${API_BASE}/api/short-term-rentals/expenses/${editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to update entry');
      }

      const data = await response.json();
      setExpenses((prev) => prev.map((exp) => (exp.id === editingId ? data.expense : exp)));
      cancelEdit();
    } catch (err) {
      setError(err.message || 'Failed to update entry');
    } finally {
      setEditSaving(false);
    }
  };

  const formatAmount = (value) => (value === null || value === undefined ? '-' : `€${Number(value).toFixed(2)}`);

  const filteredExpenses = useMemo(() => {
    return expenses.filter((exp) => {
      if (!fixedKey && propertyFilter !== 'all' && exp.property_key !== propertyFilter) return false;
      if (filterType !== 'all' && exp.expense_type !== filterType) return false;
      if (filterKind !== 'all' && exp.transaction_type !== filterKind) return false;
      const rowDate = exp.date ? String(exp.date).slice(0, 10) : '';
      if (filterFrom && rowDate < filterFrom) return false;
      if (filterTo && rowDate > filterTo) return false;
      return true;
    });
  }, [expenses, fixedKey, propertyFilter, filterType, filterKind, filterFrom, filterTo]);

  const summary = useMemo(() => {
    const totals = {
      privateIncome: 0, companyIncome: 0,
      privateExpense: 0, companyExpense: 0
    };
    filteredExpenses.forEach((exp) => {
      const value = Number(exp.amount_without_vat) || 0;
      const isCompany = exp.expense_type === 'company';
      const isIncome = exp.transaction_type === 'income';
      if (isIncome && isCompany) totals.companyIncome += value;
      else if (isIncome && !isCompany) totals.privateIncome += value;
      else if (!isIncome && isCompany) totals.companyExpense += value;
      else totals.privateExpense += value;
    });
    const totalIncome = totals.privateIncome + totals.companyIncome;
    const totalExpense = totals.privateExpense + totals.companyExpense;
    return {
      ...totals,
      totalIncome,
      totalExpense,
      grandTotal: totalIncome - totalExpense
    };
  }, [filteredExpenses]);

  const handleExportPdf = () => {
    const doc = new jsPDF();
    const marginX = 14;
    let y = 18;

    doc.setFontSize(16);
    doc.text(`${property ? property.name : 'Short Term Rentals'} - Financial Report`, marginX, y);
    y += 8;

    doc.setFontSize(10);
    doc.setTextColor(100);
    const rangeLabel = `${filterFrom ? formatDateEu(filterFrom) : 'start'} to ${filterTo ? formatDateEu(filterTo) : 'today'}`;
    const typeLabel = filterType === 'all' ? 'All (Private + Company)' : filterType === 'company' ? 'Company only' : 'Private only';
    doc.text(`Date range: ${rangeLabel}`, marginX, y);
    y += 5;
    doc.text(`Filter: ${typeLabel}`, marginX, y);
    y += 5;
    // Only the combined page reports on more than one property.
    const showPropertyColumn = !fixedKey && propertyFilter === 'all';
    if (!fixedKey) {
      doc.text(`Property: ${propertyFilter === 'all' ? 'All properties' : getStrProperty(propertyFilter)?.name}`, marginX, y);
      y += 5;
    }
    doc.text(`Generated: ${new Date().toLocaleString()}`, marginX, y);
    y += 10;

    doc.setTextColor(0);
    doc.setFontSize(9);
    const colX = { date: marginX, desc: marginX + 22, property: marginX + 60, type: marginX + 92, kind: marginX + 118, wvat: marginX + 140, wovat: marginX + 168 };
    const headerY = y;
    doc.setFont(undefined, 'bold');
    doc.text('Date', colX.date, headerY);
    doc.text('Description', colX.desc, headerY);
    if (showPropertyColumn) doc.text('Property', colX.property, headerY);
    doc.text('Type', colX.type, headerY);
    doc.text('Kind', colX.kind, headerY);
    doc.text('W/ VAT', colX.wvat, headerY);
    doc.text('W/o VAT', colX.wovat, headerY);
    doc.setFont(undefined, 'normal');
    y += 5;
    doc.setLineWidth(0.1);
    doc.line(marginX, y - 3, 196, y - 3);

    filteredExpenses.forEach((exp) => {
      if (y > 275) {
        doc.addPage();
        y = 18;
      }
      const rowDate = formatDateEu(exp.date);
      const desc = String(exp.description || '').slice(0, showPropertyColumn ? 20 : 40);
      doc.text(rowDate, colX.date, y);
      doc.text(desc, colX.desc, y);
      if (showPropertyColumn) doc.text(getStrProperty(exp.property_key)?.name || '', colX.property, y);
      doc.text(exp.expense_type === 'company' ? 'Company' : 'Private', colX.type, y);
      doc.text(exp.transaction_type === 'income' ? 'Income' : 'Expense', colX.kind, y);
      doc.text(formatAmount(exp.amount_with_vat), colX.wvat, y);
      doc.text(formatAmount(exp.amount_without_vat), colX.wovat, y);
      y += 6;
    });

    y += 6;
    if (y > 270) {
      doc.addPage();
      y = 18;
    }
    doc.setLineWidth(0.3);
    doc.line(marginX, y - 4, 196, y - 4);
    doc.setFont(undefined, 'bold');
    doc.text('Income', marginX, y);
    y += 6;
    doc.setFont(undefined, 'normal');
    doc.text(`Private income: ${formatAmount(summary.privateIncome)}`, marginX, y);
    y += 6;
    doc.text(`Company income: ${formatAmount(summary.companyIncome)}`, marginX, y);
    y += 6;
    doc.text(`Total income: ${formatAmount(summary.totalIncome)}`, marginX, y);
    y += 8;

    doc.setFont(undefined, 'bold');
    doc.text('Expenses', marginX, y);
    y += 6;
    doc.setFont(undefined, 'normal');
    doc.text(`Private expenses: ${formatAmount(summary.privateExpense)}`, marginX, y);
    y += 6;
    doc.text(`Company expenses: ${formatAmount(summary.companyExpense)}`, marginX, y);
    y += 6;
    doc.text(`Total expenses: ${formatAmount(summary.totalExpense)}`, marginX, y);
    y += 8;

    doc.setFont(undefined, 'bold');
    doc.text(`Grand total: ${formatAmount(summary.grandTotal)}`, marginX, y);

    doc.save(`${property ? property.key : 'short-term-rentals'}-financials-${todayIso()}.pdf`);
  };

  return (
    <div>
      <button onClick={() => navigate(strBasePath(property))} style={backButtonStyle}>
        <ArrowLeft size={16} /> {property ? `Back to ${property.name}` : 'Back to Short Term Rentals'}
      </button>

      <h1 style={{ color: '#1e293b', marginBottom: '10px' }}>Financials</h1>
      <p style={{ color: '#64748b', marginBottom: '30px' }}>
        Track private and company income &amp; expenses for {property ? property.siteName : 'all your short term rentals'}.
      </p>

      <form onSubmit={handleSubmit} style={formCardStyle}>
        {!property && (
          <div style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>Property</label>
            <PropertySelect value={formProperty} onChange={setFormProperty} />
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setTransactionType('expense')} style={transactionType === 'expense' ? toggleActiveStyle : toggleStyle}>
            Expense
          </button>
          <button type="button" onClick={() => setTransactionType('income')} style={transactionType === 'income' ? incomeActiveStyle : toggleStyle}>
            Income
          </button>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button
            type="button"
            onClick={() => setExpenseType('private')}
            style={expenseType === 'private' ? toggleActiveStyle : toggleStyle}
          >
            Private
          </button>
          <button
            type="button"
            onClick={() => setExpenseType('company')}
            style={expenseType === 'company' ? toggleActiveStyle : toggleStyle}
          >
            Company
          </button>
        </div>

        <div style={fieldRowStyle}>
          <div style={{ flex: 2, minWidth: '220px' }}>
            <label style={labelStyle}>Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={transactionType === 'income' ? 'What was the income for?' : 'What was the expense for?'}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1, minWidth: '150px' }}>
            <label style={labelStyle}>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        {expenseType === 'company' && (
          <div style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>Amount entry mode</label>
            <div style={{ display: 'flex', gap: '20px', marginTop: '6px', flexWrap: 'wrap' }}>
              <label style={radioLabelStyle}>
                <input
                  type="radio"
                  checked={vatMode === 'withVat'}
                  onChange={() => setVatMode('withVat')}
                />
                Enter amount with VAT (22%)
              </label>
              <label style={radioLabelStyle}>
                <input
                  type="radio"
                  checked={vatMode === 'withoutVat'}
                  onChange={() => setVatMode('withoutVat')}
                />
                Enter amount without VAT (no VAT applicable)
              </label>
            </div>
          </div>
        )}

        <div style={fieldRowStyle}>
          <div style={{ flex: 1, minWidth: '150px' }}>
            <label style={labelStyle}>
              {expenseType === 'company'
                ? (vatMode === 'withVat' ? 'Amount (with VAT)' : 'Amount (without VAT)')
                : 'Amount'}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              placeholder="0.00"
              style={inputStyle}
            />
          </div>

          {expenseType === 'company' && hasValidAmount && (
            <div style={{ flex: 1, minWidth: '200px', display: 'flex', alignItems: 'flex-end', color: '#475569', fontSize: '14px' }}>
              {vatMode === 'withVat'
                ? <span>Without VAT: <strong>{formatAmount(amountWithoutVat)}</strong></span>
                : <span>No VAT applicable to this transaction</span>}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={labelStyle}>Invoice / receipt (optional)</label>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={fileButtonStyle}>
              <Camera size={16} /> Take Photo
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => setPendingFile(e.target.files?.[0] || null)}
                style={{ display: 'none' }}
              />
            </label>
            <label style={fileButtonStyle}>
              <Paperclip size={16} /> Choose File
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setPendingFile(e.target.files?.[0] || null)}
                style={{ display: 'none' }}
              />
            </label>
            {pendingFile && (
              <span style={{ fontSize: '13px', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {pendingFile.name}
                <button type="button" onClick={() => setPendingFile(null)} style={removeFileButtonStyle}>
                  <X size={14} />
                </button>
              </span>
            )}
          </div>
        </div>

        {error && <div style={errorStyle}>{error}</div>}

        <button type="submit" disabled={saving} style={submitButtonStyle}>
          {saving ? 'Saving...' : transactionType === 'income' ? 'Add Income' : 'Add Expense'}
        </button>
      </form>

      <div style={{ ...tableCardStyle, marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {!property && <PropertyFilter value={propertyFilter} onChange={setPropertyFilter} />}
            <div>
              <label style={labelStyle}>Type</label>
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={selectStyle}>
                <option value="all">All</option>
                <option value="private">Private</option>
                <option value="company">Company</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Kind</label>
              <select value={filterKind} onChange={(e) => setFilterKind(e.target.value)} style={selectStyle}>
                <option value="all">All</option>
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>From</label>
              <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>To</label>
              <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} style={inputStyle} />
            </div>
            {(propertyFilter !== 'all' || filterType !== 'all' || filterKind !== 'all' || filterFrom || filterTo) && (
              <button
                type="button"
                onClick={() => { setPropertyFilter('all'); setFilterType('all'); setFilterKind('all'); setFilterFrom(''); setFilterTo(''); }}
                style={clearFilterButtonStyle}
              >
                Clear filters
              </button>
            )}
          </div>
          <button type="button" onClick={handleExportPdf} style={pdfButtonStyle}>
            <FileDown size={16} /> Export PDF
          </button>
        </div>
      </div>

      <div style={{ ...tableCardStyle, width: '100%', boxSizing: 'border-box' }}>
          <h3 style={{ margin: '0 0 15px', color: '#1e293b' }}>Entries</h3>
          {loading ? (
            <p style={{ color: '#64748b' }}>Loading...</p>
          ) : filteredExpenses.length === 0 ? (
            <p style={{ color: '#64748b' }}>No entries match the current filters.</p>
          ) : isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {filteredExpenses.map((exp) => (
                <ExpenseCard
                  key={exp.id}
                  exp={exp}
                  showProperty={!property}
                  isEditing={editingId === exp.id}
                  editDraft={editDraft}
                  setEditDraft={setEditDraft}
                  editHasValidAmount={editHasValidAmount}
                  editAmountWithoutVat={editAmountWithoutVat}
                  editSaving={editSaving}
                  isExpanded={expandedId === exp.id}
                  onToggleExpand={() => setExpandedId(expandedId === exp.id ? null : exp.id)}
                  onStartEdit={() => startEdit(exp)}
                  onCancelEdit={cancelEdit}
                  onSaveEdit={handleSaveEdit}
                  onDelete={() => handleDelete(exp.id)}
                  uploadingId={uploadingId}
                  onRowFileSelected={handleRowFileSelected}
                  onDownloadAttachment={handleDownloadAttachment}
                  onDeleteAttachment={handleDeleteAttachment}
                  formatAmount={formatAmount}
                />
              ))}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {!property && <th style={thStyle}>Property</th>}
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Description</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Kind</th>
                    <th style={thStyle}>Amount (w/ VAT)</th>
                    <th style={thStyle}>Amount (w/o VAT)</th>
                    <th style={thStyle}>Invoice</th>
                    <th style={thStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map((exp) => {
                    if (editingId === exp.id && editDraft) {
                      return (
                        <tr key={exp.id} style={{ backgroundColor: '#f8fafc' }}>
                          {!property && (
                            <td style={tdStyle}>
                              <EditPropertySelect value={editDraft.propertyKey} onChange={(propertyKey) => setEditDraft({ ...editDraft, propertyKey })} />
                            </td>
                          )}
                          <td style={tdStyle}>
                            <input type="date" value={editDraft.date} onChange={(e) => setEditDraft({ ...editDraft, date: e.target.value })} style={inputStyle} />
                          </td>
                          <td style={tdStyle}>
                            <input type="text" value={editDraft.description} onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })} style={inputStyle} />
                          </td>
                          <td style={tdStyle}>
                            <select
                              value={editDraft.expenseType}
                              onChange={(e) => setEditDraft({ ...editDraft, expenseType: e.target.value })}
                              style={selectStyle}
                            >
                              <option value="private">Private</option>
                              <option value="company">Company</option>
                            </select>
                          </td>
                          <td style={tdStyle}>
                            <select
                              value={editDraft.transactionType}
                              onChange={(e) => setEditDraft({ ...editDraft, transactionType: e.target.value })}
                              style={selectStyle}
                            >
                              <option value="expense">Expense</option>
                              <option value="income">Income</option>
                            </select>
                          </td>
                          <td style={tdStyle} colSpan={2}>
                            {editDraft.expenseType === 'company' && (
                              <div style={{ display: 'flex', gap: '10px', marginBottom: '6px', fontSize: '12px' }}>
                                <label style={radioLabelStyle}>
                                  <input type="radio" checked={editDraft.vatMode === 'withVat'} onChange={() => setEditDraft({ ...editDraft, vatMode: 'withVat' })} />
                                  With VAT
                                </label>
                                <label style={radioLabelStyle}>
                                  <input type="radio" checked={editDraft.vatMode === 'withoutVat'} onChange={() => setEditDraft({ ...editDraft, vatMode: 'withoutVat' })} />
                                  No VAT
                                </label>
                              </div>
                            )}
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editDraft.amountInput}
                              onChange={(e) => setEditDraft({ ...editDraft, amountInput: e.target.value })}
                              style={inputStyle}
                            />
                            {editDraft.expenseType === 'company' && editHasValidAmount && (
                              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                {editDraft.vatMode === 'withVat'
                                  ? `Without VAT: ${formatAmount(editAmountWithoutVat)}`
                                  : 'No VAT applicable'}
                              </div>
                            )}
                          </td>
                          <td style={tdStyle}>-</td>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <button type="button" onClick={handleSaveEdit} disabled={editSaving} style={saveButtonStyle}>
                                {editSaving ? 'Saving...' : 'Save'}
                              </button>
                              <button type="button" onClick={cancelEdit} style={cancelButtonStyle}>Cancel</button>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr key={exp.id}>
                        {!property && <td style={tdStyle}><PropertyBadge propertyKey={exp.property_key} /></td>}
                        <td style={tdStyle}>{formatDateEu(exp.date)}</td>
                        <td style={tdStyle}>{exp.description}</td>
                        <td style={tdStyle}>
                          <span style={exp.expense_type === 'company' ? companyBadgeStyle : privateBadgeStyle}>
                            {exp.expense_type === 'company' ? 'Company' : 'Private'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={exp.transaction_type === 'income' ? incomeBadgeStyle : expenseBadgeStyle}>
                            {exp.transaction_type === 'income' ? 'Income' : 'Expense'}
                          </span>
                        </td>
                        <td style={tdStyle}>{formatAmount(exp.amount_with_vat)}</td>
                        <td style={tdStyle}>{formatAmount(exp.amount_without_vat)}</td>
                        <td style={tdStyle}>
                          {exp.attachment_file_name ? (
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <button
                                type="button"
                                onClick={() => handleDownloadAttachment(exp.id, exp.attachment_original_name)}
                                style={attachmentLinkStyle}
                                title={exp.attachment_original_name || 'Download attachment'}
                              >
                                <Paperclip size={14} /> View
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteAttachment(exp.id)}
                                style={removeFileButtonStyle}
                                title="Remove attachment"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <label style={{ ...fileButtonStyle, fontSize: '12px', padding: '5px 10px' }}>
                              {uploadingId === exp.id ? 'Uploading...' : <><Paperclip size={13} /> Add</>}
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                onChange={(e) => handleRowFileSelected(exp.id, e.target.files?.[0])}
                                style={{ display: 'none' }}
                                disabled={uploadingId === exp.id}
                              />
                            </label>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button onClick={() => startEdit(exp)} style={editButtonStyle}>Edit</button>
                            <button onClick={() => handleDelete(exp.id)} style={deleteButtonStyle}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && filteredExpenses.length > 0 && (
            <div style={summaryBoxStyle}>
              <SummaryContent summary={summary} formatAmount={formatAmount} />
            </div>
          )}
      </div>
    </div>
  );
};

const EditPropertySelect = ({ value, onChange }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
    {STR_PROPERTIES.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
  </select>
);

const SummaryContent = ({ summary, formatAmount }) => (
  <>
    <h4 style={summaryHeadingStyle}>Income</h4>
    <div style={summaryRowStyle}>
      <span>Private income</span>
      <strong style={{ color: '#059669' }}>{formatAmount(summary.privateIncome)}</strong>
    </div>
    <div style={summaryRowStyle}>
      <span>Company income</span>
      <strong style={{ color: '#059669' }}>{formatAmount(summary.companyIncome)}</strong>
    </div>
    <div style={{ ...summaryRowStyle, borderTop: '1px solid #e2e8f0', paddingTop: '8px', marginTop: '3px' }}>
      <span><strong>Total income</strong></span>
      <strong style={{ color: '#059669' }}>{formatAmount(summary.totalIncome)}</strong>
    </div>

    <h4 style={{ ...summaryHeadingStyle, marginTop: '18px' }}>Expenses</h4>
    <div style={summaryRowStyle}>
      <span>Private expenses</span>
      <strong style={{ color: '#dc2626' }}>{formatAmount(summary.privateExpense)}</strong>
    </div>
    <div style={summaryRowStyle}>
      <span>Company expenses</span>
      <strong style={{ color: '#dc2626' }}>{formatAmount(summary.companyExpense)}</strong>
    </div>
    <div style={{ ...summaryRowStyle, borderTop: '1px solid #e2e8f0', paddingTop: '8px', marginTop: '3px' }}>
      <span><strong>Total expenses</strong></span>
      <strong style={{ color: '#dc2626' }}>{formatAmount(summary.totalExpense)}</strong>
    </div>

    <div style={{ ...summaryRowStyle, borderTop: '2px solid #cbd5e1', paddingTop: '12px', marginTop: '15px' }}>
      <span><strong>Grand total</strong></span>
      <strong style={{ color: summary.grandTotal < 0 ? '#dc2626' : '#059669', fontSize: '16px' }}>{formatAmount(summary.grandTotal)}</strong>
    </div>
  </>
);

const ExpenseCard = ({
  exp, showProperty, isEditing, editDraft, setEditDraft, editHasValidAmount, editAmountWithoutVat, editSaving,
  isExpanded, onToggleExpand, onStartEdit, onCancelEdit, onSaveEdit, onDelete,
  uploadingId, onRowFileSelected, onDownloadAttachment, onDeleteAttachment, formatAmount
}) => {
  if (isEditing && editDraft) {
    return (
      <div style={mobileCardStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {showProperty && <EditPropertySelect value={editDraft.propertyKey} onChange={(propertyKey) => setEditDraft({ ...editDraft, propertyKey })} />}
          <input type="date" value={editDraft.date} onChange={(e) => setEditDraft({ ...editDraft, date: e.target.value })} style={inputStyle} />
          <input type="text" value={editDraft.description} onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })} style={inputStyle} placeholder="Description" />
          <div style={{ display: 'flex', gap: '10px' }}>
            <select value={editDraft.expenseType} onChange={(e) => setEditDraft({ ...editDraft, expenseType: e.target.value })} style={{ ...selectStyle, flex: 1 }}>
              <option value="private">Private</option>
              <option value="company">Company</option>
            </select>
            <select value={editDraft.transactionType} onChange={(e) => setEditDraft({ ...editDraft, transactionType: e.target.value })} style={{ ...selectStyle, flex: 1 }}>
              <option value="expense">Expense</option>
              <option value="income">Income</option>
            </select>
          </div>
          {editDraft.expenseType === 'company' && (
            <div style={{ display: 'flex', gap: '10px', fontSize: '12px' }}>
              <label style={radioLabelStyle}>
                <input type="radio" checked={editDraft.vatMode === 'withVat'} onChange={() => setEditDraft({ ...editDraft, vatMode: 'withVat' })} />
                With VAT
              </label>
              <label style={radioLabelStyle}>
                <input type="radio" checked={editDraft.vatMode === 'withoutVat'} onChange={() => setEditDraft({ ...editDraft, vatMode: 'withoutVat' })} />
                No VAT
              </label>
            </div>
          )}
          <input
            type="number"
            step="0.01"
            min="0"
            value={editDraft.amountInput}
            onChange={(e) => setEditDraft({ ...editDraft, amountInput: e.target.value })}
            style={inputStyle}
            placeholder="Amount"
          />
          {editDraft.expenseType === 'company' && editHasValidAmount && (
            <div style={{ fontSize: '12px', color: '#64748b' }}>
              {editDraft.vatMode === 'withVat' ? `Without VAT: ${formatAmount(editAmountWithoutVat)}` : 'No VAT applicable'}
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={onSaveEdit} disabled={editSaving} style={{ ...saveButtonStyle, flex: 1, padding: '10px' }}>
              {editSaving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" onClick={onCancelEdit} style={{ ...cancelButtonStyle, flex: 1, padding: '10px' }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={mobileCardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: '#1e293b', wordBreak: 'break-word' }}>{exp.description}</div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>{formatDateEu(exp.date)}</div>
        </div>
        <button type="button" onClick={onToggleExpand} style={editButtonStyle}>
          {isExpanded ? 'Close' : 'Edit'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '10px 0' }}>
        {showProperty && <PropertyBadge propertyKey={exp.property_key} />}
        <span style={exp.expense_type === 'company' ? companyBadgeStyle : privateBadgeStyle}>
          {exp.expense_type === 'company' ? 'Company' : 'Private'}
        </span>
        <span style={exp.transaction_type === 'income' ? incomeBadgeStyle : expenseBadgeStyle}>
          {exp.transaction_type === 'income' ? 'Income' : 'Expense'}
        </span>
        {exp.attachment_file_name && (
          <span style={attachmentBadgeStyle}>
            <Paperclip size={11} /> Invoice
          </span>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
        <span style={{ color: '#64748b' }}>Amount (w/o VAT)</span>
        <strong style={{ color: '#1e293b' }}>{formatAmount(exp.amount_without_vat)}</strong>
      </div>
      {exp.expense_type === 'company' && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginTop: '4px' }}>
          <span style={{ color: '#94a3b8' }}>Amount (w/ VAT)</span>
          <span style={{ color: '#64748b' }}>{formatAmount(exp.amount_with_vat)}</span>
        </div>
      )}

      {isExpanded && (
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div>
            {exp.attachment_file_name ? (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => onDownloadAttachment(exp.id, exp.attachment_original_name)}
                  style={{ ...attachmentLinkStyle, flex: 1, justifyContent: 'center', padding: '8px' }}
                >
                  <Paperclip size={14} /> View Invoice
                </button>
                <button type="button" onClick={() => onDeleteAttachment(exp.id)} style={removeFileButtonStyle} title="Remove attachment">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label style={{ ...fileButtonStyle, width: '100%', justifyContent: 'center', boxSizing: 'border-box' }}>
                {uploadingId === exp.id ? 'Uploading...' : <><Paperclip size={14} /> Add Invoice</>}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => onRowFileSelected(exp.id, e.target.files?.[0])}
                  style={{ display: 'none' }}
                  disabled={uploadingId === exp.id}
                />
              </label>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={onStartEdit} style={{ ...editButtonStyle, flex: 1, padding: '10px' }}>Edit details</button>
            <button type="button" onClick={onDelete} style={{ ...deleteButtonStyle, flex: 1, padding: '10px' }}>Delete</button>
          </div>
        </div>
      )}
    </div>
  );
};

const backButtonStyle = { display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '14px', marginBottom: '20px', padding: 0 };
const formCardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0', marginBottom: '30px', maxWidth: '700px' };
const fieldRowStyle = { display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' };
const labelStyle = { display: 'block', fontSize: '13px', color: '#475569', marginBottom: '5px', fontWeight: 600 };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', boxSizing: 'border-box' };
const selectStyle = { ...inputStyle, width: 'auto', minWidth: '140px' };
const radioLabelStyle = { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#334155', cursor: 'pointer' };
const toggleStyle = { padding: '8px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#475569', cursor: 'pointer', fontWeight: 600 };
const toggleActiveStyle = { ...toggleStyle, backgroundColor: '#1e293b', color: 'white', borderColor: '#1e293b' };
const incomeActiveStyle = { ...toggleStyle, backgroundColor: '#059669', color: 'white', borderColor: '#059669' };
const submitButtonStyle = { padding: '10px 24px', borderRadius: '8px', border: 'none', backgroundColor: '#059669', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: '14px' };
const errorStyle = { color: '#dc2626', fontSize: '14px', marginBottom: '15px' };
const tableCardStyle = { backgroundColor: 'white', padding: '25px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '14px' };
const thStyle = { textAlign: 'left', padding: '10px', borderBottom: '2px solid #e2e8f0', color: '#475569' };
const tdStyle = { padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#1e293b' };
const deleteButtonStyle = { padding: '5px 10px', borderRadius: '6px', border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '12px' };
const editButtonStyle = { padding: '5px 10px', borderRadius: '6px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontSize: '12px' };
const saveButtonStyle = { padding: '5px 10px', borderRadius: '6px', border: 'none', backgroundColor: '#059669', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: 600 };
const cancelButtonStyle = { padding: '5px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#475569', cursor: 'pointer', fontSize: '12px' };
const summaryHeadingStyle = { margin: '0 0 6px', fontSize: '13px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' };
const fileButtonStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#475569', cursor: 'pointer', fontSize: '13px', fontWeight: 600 };
const removeFileButtonStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '4px', borderRadius: '6px', border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#dc2626', cursor: 'pointer' };
const attachmentLinkStyle = { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontSize: '12px' };
const companyBadgeStyle = { padding: '3px 10px', borderRadius: '999px', backgroundColor: '#f5f3ff', color: '#7c3aed', fontSize: '12px', fontWeight: 600 };
const privateBadgeStyle = { padding: '3px 10px', borderRadius: '999px', backgroundColor: '#eff6ff', color: '#2563eb', fontSize: '12px', fontWeight: 600 };
const incomeBadgeStyle = { padding: '3px 10px', borderRadius: '999px', backgroundColor: '#ecfdf5', color: '#059669', fontSize: '12px', fontWeight: 600 };
const expenseBadgeStyle = { padding: '3px 10px', borderRadius: '999px', backgroundColor: '#fef2f2', color: '#dc2626', fontSize: '12px', fontWeight: 600 };
const clearFilterButtonStyle = { padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#475569', cursor: 'pointer', fontSize: '13px' };
const pdfButtonStyle = { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', borderRadius: '8px', border: 'none', backgroundColor: '#1e293b', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 600 };
const summaryBoxStyle = { marginTop: '20px', paddingTop: '15px', borderTop: '2px solid #e2e8f0', maxWidth: '350px', marginRight: 'auto' };
const summaryRowStyle = { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '14px', color: '#334155' };
const mobileCardStyle = { backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px' };
const attachmentBadgeStyle = { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '999px', backgroundColor: '#fefce8', color: '#a16207', fontSize: '12px', fontWeight: 600 };

export default FinancialsPage;
